import { ArrowRightOutlined, CheckOutlined, MailOutlined, InstagramOutlined } from '@ant-design/icons'
import { Button } from 'antd'
import { useEffect, useState, type CSSProperties } from 'react'
import Navbar from '../components/Navbar'
import BrandLogo from '../components/BrandLogo'
import ContactForm from '../components/ContactForm'
import { trackConversion } from '../lib/analytics'
import type { Locale } from '../hooks/useLocale'
import DominicanFlagIcon from '../icons/do.flag'

type Props = {
  locale: Locale
  setLocale: (v: Locale) => void
  copy: any
}

export default function LandingPage({ locale, setLocale, copy }: Props) {
  const [parallaxY, setParallaxY] = useState(0)

  useEffect(() => {
    let frame = 0
    const updateParallax = () => {
      frame = 0
      setParallaxY(Math.min(window.scrollY * 0.1, 42))
    }
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(updateParallax)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      if (frame) window.cancelAnimationFrame(frame)
    }
  }, [])

  const scroll = (id: string, event?: string) => {
    if (event) trackConversion('cta_click', { placement: event })
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <div id="top" className="siteRoot">
      <Navbar locale={locale} setLocale={setLocale} copy={copy} />

      <main>
        <section className="hero" aria-labelledby="hero-title">
          <div className="hero__gridLines" aria-hidden="true" />
          <div className="container hero__layout">
            <div className="hero__copy">
              <p className="kicker kicker--light">{copy.hero.kicker}  <DominicanFlagIcon /></p>
              <h1 id="hero-title">{copy.hero.title}</h1>
              <p className="hero__lead">{copy.hero.body}</p>
              <div className="hero__actions">
                <Button type="primary" size="large" className="goldBtn" onClick={() => scroll('contact', 'hero_primary')}>
                  {copy.hero.primary} <ArrowRightOutlined />
                </Button>
                <button className="textButton textButton--light" onClick={() => scroll('approach', 'hero_secondary')}>
                  {copy.hero.secondary} <span aria-hidden="true">↓</span>
                </button>
              </div>
              <p className="hero__context">{copy.hero.context}</p>
            </div>

            <div className="heroVisual" style={{ '--parallax-y': `${parallaxY}px` } as CSSProperties}>
              <div className="heroVisual__imageFrame">
                <img src="/assets/strategy-session.jpg" alt={copy.media.heroAlt} />
                <div className="heroVisual__wash" aria-hidden="true" />
              </div>
              <div className="executionMap" aria-label={copy.hero.visualLabel}>
                <div className="executionMap__header">
                  <span>{copy.hero.visualEyebrow}</span>
                  <i />
                </div>
                {copy.executionPath.map((step: string, index: number) => (
                  <div className="executionMap__row" key={step}>
                    <span className="executionMap__number">0{index + 1}</span>
                    <strong>{step}</strong>
                    {index < copy.executionPath.length - 1 && <span className="executionMap__arrow" aria-hidden="true">↘</span>}
                  </div>
                ))}
                <div className="executionMap__result">{copy.hero.visualResult}</div>
              </div>
            </div>
          </div>
        </section>

        <section id="problem" className="section problemSection" aria-labelledby="problem-title">
          <div className="container splitHeading">
            <div>
              <p className="kicker">{copy.problem.kicker}</p>
              <h2 id="problem-title">{copy.problem.title}</h2>
            </div>
            <p className="sectionLead">{copy.problem.intro}</p>
          </div>
          <div className="container problemGrid">
            {copy.problem.items.map((item: string, index: number) => (
              <article className="problemItem" key={item}>
                <span>0{index + 1}</span>
                <p>{item}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="section thinkingSection" aria-labelledby="thinking-title">
          <div className="container thinkingLayout">
            <div>
              <p className="kicker">{copy.thinking.kicker}</p>
              <h2 id="thinking-title">{copy.thinking.title}</h2>
              <p className="sectionLead">{copy.thinking.body}</p>
            </div>
            <div className="thinkingPath" aria-label={copy.thinking.pathLabel}>
              {copy.thinking.path.map((item: string, index: number) => (
                <div className="thinkingPath__item" key={item}>
                  <span>{item}</span>
                  {index < copy.thinking.path.length - 1 && <i aria-hidden="true">→</i>}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="services" className="section solutionsSection" aria-labelledby="solutions-title">
          <div className="container splitHeading">
            <div>
              <p className="kicker">{copy.solutions.kicker}</p>
              <h2 id="solutions-title">{copy.solutions.title}</h2>
            </div>
            <p className="sectionLead">{copy.solutions.intro}</p>
          </div>
          <div className="container solutionGrid">
            {copy.solutions.items.map((solution: any, index: number) => (
              <article className={`solutionCard`} key={solution.title}>
                <div className="solutionCard__topline">
                  <span>0{index + 1}</span>
                  <small>{solution.label}</small>
                </div>
                <h3>{solution.title}</h3>
                <p>{solution.body}</p>
                <ul>
                  {solution.points.map((point: string) => <li key={point}><CheckOutlined /> {point}</li>)}
                </ul>
                {index === 0 && <button className="textButton" onClick={() => scroll('contact', 'diagnostic_card')}>{copy.solutions.diagnosticCta} <ArrowRightOutlined /></button>}
              </article>
            ))}
          </div>
        </section>

        <section id="approach" className="section approachSection" aria-labelledby="approach-title">
          <div className="container splitHeading splitHeading--light">
            <div>
              <p className="kicker kicker--light">{copy.approach.kicker}</p>
              <h2 id="approach-title">{copy.approach.title}</h2>
            </div>
            <p className="sectionLead">{copy.approach.intro}</p>
          </div>
          <div className="container deliveryFlow">
            {copy.approach.steps.map((step: any, index: number) => (
              <div className="deliveryStep" key={step.title}>
                <span>0{index + 1}</span>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
                {index < copy.approach.steps.length - 1 && <div className="deliveryStep__connector" aria-hidden="true">→</div>}
              </div>
            ))}
          </div>
          <p className="container approachPrinciple">{copy.approach.principle}</p>
        </section>

        <section className="section outcomesSection" aria-labelledby="outcomes-title">
          <div className="container splitHeading">
            <div>
              <p className="kicker">{copy.outcomes.kicker}</p>
              <h2 id="outcomes-title">{copy.outcomes.title}</h2>
            </div>
            <p className="sectionLead">{copy.outcomes.intro}</p>
          </div>
          <div className="container outcomeGrid">
            {copy.outcomes.items.map((outcome: any) => (
              <article className="outcomeCard" key={outcome.title}>
                <h3>{outcome.title}</h3>
                <ul>{outcome.points.map((point: string) => <li key={point}>{point}</li>)}</ul>
              </article>
            ))}
          </div>
        </section>

        <section id="who-we-serve" className="section whoSection" aria-labelledby="who-title">
          <div className="container whoLayout">
            <div>
              <p className="kicker">{copy.who.kicker}</p>
              <h2 id="who-title">{copy.who.title}</h2>
              <p className="sectionLead">{copy.who.body}</p>
            </div>
            <div className="whoSidebar">
              <figure className="whoImage">
                <img src="/assets/leadership-dialogue.jpg" alt={copy.media.whoAlt} loading="lazy" />
              </figure>
              <ul className="situationList">
                {copy.who.situations.map((situation: string) => <li key={situation}><span>+</span>{situation}</li>)}
              </ul>
            </div>
          </div>
        </section>

        <section className="section distinctionSection" aria-labelledby="distinction-title">
          <div className="container distinctionLayout">
            <div>
              <p className="kicker">{copy.distinction.kicker}</p>
              <h2 id="distinction-title">{copy.distinction.title}</h2>
            </div>
            <div className="distinctionList">
              {copy.distinction.items.map((item: string) => <p key={item}>{item}</p>)}
              <strong>{copy.distinction.conclusion}</strong>
            </div>
          </div>
        </section>

        <section className="ctaSection" aria-labelledby="cta-title">
          <div className="container ctaSection__inner">
            <div>
              <p className="kicker kicker--light">{copy.cta.kicker}</p>
              <h2 id="cta-title">{copy.cta.title}</h2>
              <p>{copy.cta.body}</p>
            </div>
            <Button type="primary" size="large" className="goldBtn" onClick={() => scroll('contact', 'mid_page_cta')}>
              {copy.cta.button} <ArrowRightOutlined />
            </Button>
          </div>
        </section>

        <section id="contact" className="section contactSection" aria-labelledby="contact-title">
          <div className="container contactGrid">
            <div className="contactCopy">
              <p className="kicker">{copy.contact.kicker}</p>
              <h2 id="contact-title">{copy.contact.title}</h2>
              <p className="sectionLead">{copy.contact.body}</p>
            </div>
            <ContactForm copy={copy} />
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="container footer__inner">
          <div><BrandLogo light /><p>{copy.footer.tagline}</p></div>
          <div className="footer__links">
            <button onClick={() => scroll('services')}>{copy.nav.services}</button>
            <button onClick={() => scroll('approach')}>{copy.nav.approach}</button>
            <button onClick={() => scroll('contact')}>{copy.nav.contact}</button>
          </div>
          <div className="footer__meta">
            <div className="footer__socials" aria-label="TALENXA social links">
              <a href="https://www.instagram.com/talenxard" target="_blank" rel="noopener noreferrer" className="socialBtn socialBtn--light" aria-label="Instagram"><InstagramOutlined /></a>
              <a href="mailto:hello@talenxa.com.do" className="socialBtn socialBtn--light" aria-label="Email TALENXA"><MailOutlined /></a>
            </div>
            <small>© {new Date().getFullYear()} TALENXA. {copy.footer.rights}</small>
          </div>
        </div>
      </footer>
    </div>
  )
}
